<template>
  <div class="space-y-6">
    <hs-card class="space-y-4">
      <div class="flex items-center justify-between gap-3">
        <div><h2 class="font-semibold">Subscription notifications</h2><p class="text-sm text-gray-500">New subscription confirmations, renewal confirmations, and expiration reminders by email and SMS.</p></div>
        <hs-button variant="secondary" icon="refresh-cw" :loading="loading" @click="load">Refresh</hs-button>
      </div>
      <hs-alert v-if="loadError" tone="danger" :message="loadError" />
      <form v-if="settingsLoaded" class="space-y-4" @submit.prevent="saveSettings">
        <label class="flex items-center gap-2 text-sm"><input v-model="settings.enabled" type="checkbox" :disabled="savingSettings" /> Enable subscription notifications</label>
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <hs-input v-model="settings.excludedStores" label="Excluded store IDs" placeholder="1, 2, 3" hint="Separate IDs with commas. These stores receive no subscription messages." :disabled="savingSettings" />
          <hs-input v-model="settings.excludedGroups" label="Excluded group IDs" placeholder="1, 2, 3" hint="Separate IDs with commas. These groups receive no subscription messages." :disabled="savingSettings" />
        </div>
        <hs-alert v-if="settingsError" tone="danger" :message="settingsError" />
        <hs-button type="submit" :loading="savingSettings" :disabled="loading">Save notification settings</hs-button>
        <p v-if="settingsSaved" role="status" class="text-sm text-emerald-600">Notification settings saved.</p>
      </form>
    </hs-card>

    <hs-table-shell title="Message templates" description="Edit English and Kinyarwanda copy and preview messages before publishing a new version.">
      <hs-alert v-if="templateError" tone="danger" :message="templateError" />
      <table v-if="activeTemplates.length" class="w-full text-left text-sm">
        <thead><tr><th class="p-3">Message</th><th class="p-3">Channel</th><th class="p-3">Version</th><th class="p-3">Languages</th><th class="p-3">Actions</th></tr></thead>
        <tbody><tr v-for="template in activeTemplates" :key="template.id" class="border-t">
          <td class="p-3">{{ templateLabel(template.key) }}</td><td class="p-3">{{ template.channel }}</td><td class="p-3">{{ template.version }}</td>
          <td class="p-3">{{ template.available_languages?.join(', ') || Object.keys(template.localized_variants || {}).join(', ') }}</td>
          <td class="p-3"><hs-button variant="ghost" size="sm" icon="edit-2" @click="editingTemplate = template">Edit and preview</hs-button></td>
        </tr></tbody>
      </table>
      <p v-else-if="!loading && !templateError" class="p-4 text-sm text-gray-500">No active subscription message templates.</p>
    </hs-table-shell>

    <hs-table-shell title="Delivery history" description="Check pending, sent, and failed notifications. Retry timing and errors are reported by the backend.">
      <template #filters>
        <form class="flex flex-wrap items-end gap-3" @submit.prevent="searchDeliveries">
          <hs-input v-model="filters.query" label="Search" placeholder="Recipient or message" />
          <hs-select v-model="filters.template_key" label="Message" :options="messageOptions" />
          <hs-select v-model="filters.channel" label="Channel" :options="channelOptions" />
          <hs-select v-model="filters.status" label="Status" :options="deliveryStatusOptions" />
          <hs-button type="submit" variant="secondary" :loading="loadingDeliveries">Search</hs-button>
        </form>
      </template>
      <hs-alert v-if="deliveryError" tone="danger" :message="deliveryError" />
      <table v-if="deliveries.length" class="w-full text-left text-sm">
        <thead><tr><th class="p-3">Created</th><th class="p-3">Message / recipient</th><th class="p-3">Channel</th><th class="p-3">Status</th><th class="p-3">Delivery details</th></tr></thead>
        <tbody><tr v-for="delivery in deliveries" :key="delivery.id" class="border-t">
          <td class="p-3 whitespace-nowrap">{{ dateTime(delivery.created_at) }}</td>
          <td class="p-3"><p>{{ templateLabel(delivery.template_key) }}</p><p class="text-gray-500">{{ delivery.recipient }} · {{ delivery.selected_language }}</p>
            <details class="mt-1"><summary class="cursor-pointer">Message content</summary><p v-if="delivery.subject" class="font-medium">{{ delivery.subject }}</p><pre class="whitespace-pre-wrap break-words text-xs">{{ delivery.rendered_content }}</pre></details>
          </td>
          <td class="p-3">{{ delivery.channel }}</td>
          <td class="p-3"><hs-badge :tone="delivery.status === 'SENT' ? 'success' : delivery.status === 'FAILED' ? 'danger' : 'warning'">{{ delivery.status }}</hs-badge></td>
          <td class="p-3"><p>Attempts: {{ delivery.attempts }}</p><p v-if="delivery.sent_at">Sent: {{ dateTime(delivery.sent_at) }}</p><p v-if="delivery.error_message" class="text-red-600">{{ delivery.error_code }} {{ delivery.error_message }}</p><p v-if="delivery.retryable && delivery.next_attempt_at">Next retry: {{ dateTime(delivery.next_attempt_at) }}</p></td>
        </tr></tbody>
      </table>
      <p v-else-if="!loadingDeliveries && !deliveryError" class="p-4 text-sm text-gray-500">No deliveries match these filters.</p>
      <template #footer>
        <div class="flex items-center justify-between gap-3 text-sm">
          <span>{{ total }} deliveries · Page {{ page }} of {{ pages || 1 }}</span>
          <div class="flex gap-2"><hs-button variant="secondary" size="sm" :disabled="page <= 1 || loadingDeliveries" @click="changePage(-1)">Previous</hs-button><hs-button variant="secondary" size="sm" :disabled="page >= pages || loadingDeliveries" @click="changePage(1)">Next</hs-button></div>
        </div>
      </template>
    </hs-table-shell>
    <subscription-message-template-modal v-if="editingTemplate" :template="editingTemplate" @close="editingTemplate = null" @saved="templateSaved" />
  </div>
</template>

<script>
import { HsCard, HsTableShell, HsInput, HsSelect, HsButton, HsAlert, HsBadge } from "@/components/ui";
import SubscriptionMessageTemplateModal from "./SubscriptionMessageTemplateModal.vue";
import http from "@/helper/http";
import api from "@/helper/api";

const messageOptions = [
  { value: "subscription_expiration_reminder", label: "Expiration reminder" },
  { value: "subscription_new_confirmation", label: "New subscription confirmation" },
  { value: "subscription_renewal_confirmation", label: "Renewal confirmation" },
];
const ids = (text) => {
  if (!text.trim()) return [];
  const values = text.trim().split(/[\s,]+/).map(Number);
  if (values.some((value) => !Number.isSafeInteger(value) || value < 1)) throw new Error("Excluded IDs must be positive whole numbers separated by commas.");
  return [...new Set(values)];
};
export default {
  name: "SubscriptionMessagingPanel",
  components: { HsCard, HsTableShell, HsInput, HsSelect, HsButton, HsAlert, HsBadge, SubscriptionMessageTemplateModal },
  data: () => ({
    loading: false, loadError: "", templateError: "", templates: [], editingTemplate: null,
    settingsLoaded: false, settings: { enabled: false, excludedStores: "", excludedGroups: "" }, originalFlag: {},
    savingSettings: false, settingsError: "", settingsSaved: false,
    deliveryRequestId: 0, deliveries: [], loadingDeliveries: false, deliveryError: "", page: 1, pages: 0, total: 0,
    filters: { query: "", template_key: messageOptions[0].value, channel: "", status: "" },
    messageOptions,
    channelOptions: [{ label: "All channels", value: "" }, { label: "Email", value: "EMAIL" }, { label: "SMS", value: "SMS" }],
    deliveryStatusOptions: [{ label: "All statuses", value: "" }, ...["PENDING", "SENT", "FAILED"].map((value) => ({ label: value, value }))],
  }),
  computed: {
    activeTemplates() { return this.templates.filter((template) => template.is_active && messageOptions.some((item) => item.value === template.key)); },
  },
  mounted() { this.load(); },
  methods: {
    templateLabel(key) { return messageOptions.find((item) => item.value === key)?.label || key; },
    dateTime(value) { return value ? new Date(value).toLocaleString() : "—"; },
    async load() {
      if (this.loading) return;
      this.loading = true; this.loadError = ""; this.templateError = ""; this.settingsLoaded = false;
      const results = await Promise.allSettled([http.get(api.admin_feature_flags), http.get("/v1/admin/messaging/templates")]);
      const [flags, templates] = results;
      if (flags.status === "fulfilled") {
        this.originalFlag = (flags.value.data || []).find((flag) => flag.key === "SUBSCRIPTION_NOTIFICATIONS") || {};
        this.settings = { enabled: Boolean(this.originalFlag.enabled), excludedStores: (this.originalFlag.metadata?.excluded_store_ids || []).join(", "), excludedGroups: (this.originalFlag.metadata?.excluded_group_ids || []).join(", ") };
        this.settingsLoaded = true;
      } else this.loadError = flags.reason?.response?.data?.error?.message || "Unable to load notification settings.";
      if (templates.status === "fulfilled") this.templates = templates.value.data?.templates || [];
      else this.templateError = templates.reason?.response?.data?.error?.message || "Unable to load message templates.";
      this.loading = false;
      await this.loadDeliveries();
    },
    async saveSettings() {
      if (this.savingSettings || !this.settingsLoaded) return;
      this.savingSettings = true; this.settingsError = ""; this.settingsSaved = false;
      try {
        const { data } = await http.patch(api.admin_feature_flag("SUBSCRIPTION_NOTIFICATIONS"), {
          enabled: this.settings.enabled,
          description: this.originalFlag.description || "Subscription confirmations and expiration reminders by email and SMS.",
          metadata: { ...this.originalFlag.metadata, excluded_store_ids: ids(this.settings.excludedStores), excluded_group_ids: ids(this.settings.excludedGroups) },
        });
        this.originalFlag = data; this.settingsSaved = true;
      } catch (error) { this.settingsError = error?.response?.data?.error?.message || error.message || "Unable to save notification settings."; }
      finally { this.savingSettings = false; }
    },
    async templateSaved() { this.editingTemplate = null; await this.load(); },
    async searchDeliveries() { this.page = 1; await this.loadDeliveries(); },
    async changePage(delta) { this.page += delta; await this.loadDeliveries(); },
    async loadDeliveries() {
      const requestId = ++this.deliveryRequestId;
      this.loadingDeliveries = true; this.deliveryError = "";
      try {
        const { data } = await http.get("/v1/admin/messaging/deliveries", { params: { ...this.filters, page: this.page, per_page: 20 } });
        if (requestId !== this.deliveryRequestId) return;
        this.deliveries = data.items || []; this.pages = data.pages || 0; this.total = data.total || 0;
      } catch (error) { if (requestId === this.deliveryRequestId) this.deliveryError = error?.response?.data?.error?.message || "Unable to load delivery history."; }
      finally { if (requestId === this.deliveryRequestId) this.loadingDeliveries = false; }
    },
  },
};
</script>
